import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { S1000D_ISSUES, BR_CATEGORY_DM_MAP, DM_TYPES } from "../lib/s1000dData";
import { fmtCurrency, fmtHours, fmtPercent, brexIssueDelta } from "../lib/calculations";
import { exportBrexSkeleton } from "../lib/brexSkeleton";
import { CheckSquare, Square, RotateCcw, Plus, Trash2, FileCode2, BookOpenCheck, Download, ArrowLeftRight, GitMerge } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
export default function BrexTab() {
  const { t } = useTranslation();
  const {
    estimate, brex, formatCurrency,
    updateBrexPlan, toggleBrexCategory, setBrexCategoryDps,
    updateBrexRole, addBrexRole, removeBrexRole, resetBrexRoles,
    setAllBrexCategoriesIncluded,
  } = useEstimate();
  const plan = estimate.brexPlan;
  const sym = estimate.currency?.symbol || "$";

  const effortSum = plan.roles.reduce((s, r) => s + (Number(r.effortPercent) || 0), 0);
  const effortValid = Math.abs(effortSum - 100) < 0.01;

  // Issue-compare state — defaults to the next/previous issue
  const issueIndex = S1000D_ISSUES.indexOf(plan.s1000dIssue);
  const defaultCompare = S1000D_ISSUES[issueIndex - 1] || S1000D_ISSUES[issueIndex + 1] || S1000D_ISSUES[0];
  const [compareIssue, setCompareIssue] = useState(defaultCompare);
  const delta = brexIssueDelta(plan, plan.s1000dIssue, compareIssue);

  // DM coverage — types actually used by the estimate (qty > 0)
  const usedDmTypes = new Set();
  for (const code of Object.keys(estimate.dmMatrix || {})) {
    const row = estimate.dmMatrix[code];
    const tot = (Number(row?.simple?.qty) || 0) + (Number(row?.intermediate?.qty) || 0) + (Number(row?.complex?.qty) || 0);
    if (tot > 0) usedDmTypes.add(code);
  }
  const codeToName = Object.fromEntries(DM_TYPES.map((t) => [t.code, t.name]));

  return (
    <section className="space-y-6" data-testid="brex-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("brex.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("brex.title")}
            <HelpIconButton anchor="ch2" testId="help-icon-brex" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            {t("brex.description")}
          </p>
        </div>
        <div className="border border-gray-300 px-4 py-3 bg-white min-w-[220px]" data-testid="brex-grand-total-card">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            BR &amp; BREX Grand Total
          </p>
          <p className="font-mono text-2xl font-bold text-[#002FA7] leading-none mt-1">
            {formatCurrency(brex.grandTotal)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
            {fmtHours(brex.totalHours)} hrs · {brex.totalDps} DPs
          </p>
        </div>
      </div>

      {/* CONTROLS */}
      <div className="bg-white border border-gray-300 p-5 space-y-4" data-testid="brex-controls">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              S1000D Issue
            </label>
            <select
              data-testid="brex-issue-select"
              value={plan.s1000dIssue}
              onChange={(e) => updateBrexPlan("s1000dIssue", e.target.value)}
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full bg-white"
            >
              {S1000D_ISSUES.map((i) => (
                <option key={i} value={i}>Issue {i}{i === "6.0" ? " (latest)" : ""}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              Each issue ships a different baseline of decision points.
            </p>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              Define · min / DP
            </label>
            <input
              type="number" min="0" step="0.5"
              value={plan.defineMinutesPerDP}
              onChange={(e) => updateBrexPlan("defineMinutesPerDP", Number(e.target.value) || 0)}
              data-testid="brex-define-mins-input"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full text-right"
            />
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              Time to define a business rule
            </p>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              BREX · min / DP
            </label>
            <input
              type="number" min="0" step="0.5"
              value={plan.brexMinutesPerDP}
              onChange={(e) => updateBrexPlan("brexMinutesPerDP", Number(e.target.value) || 0)}
              data-testid="brex-brex-mins-input"
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm w-full text-right"
            />
            <p className="text-[10px] text-gray-500 mt-1 font-mono">
              Time to author the BREX path statement
            </p>
          </div>
          <div className="border border-gray-300 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              Adjustments
            </p>
            <p className="font-mono text-xs mt-2">
              <span className="text-gray-500 mr-1">Overhead</span>
              {fmtPercent(estimate.overheadPercent)} ·
              <span className="text-gray-500 ml-2 mr-1">Contingency</span>
              {fmtPercent(estimate.contingencyPercent)}
            </p>
            <p className="text-[10px] text-gray-500 mt-2 font-mono">
              Edit on Roles &amp; Rates tab.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAllBrexCategoriesIncluded(true)} data-testid="brex-include-all-btn"
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <CheckSquare size={12} /> Include All
          </button>
          <button onClick={() => setAllBrexCategoriesIncluded(false)} data-testid="brex-exclude-all-btn"
            className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-3 py-1.5 font-bold text-xs inline-flex items-center gap-1">
            <Square size={12} /> Exclude All
          </button>
        </div>
      </div>

      {/* CATEGORY TABLE */}
      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="brex-category-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3 w-24">Include</th>
              <th className="py-3 px-3">Category</th>
              <th className="py-3 px-3 text-right">Default DPs</th>
              <th className="py-3 px-3 text-right">DPs (editable)</th>
              <th className="py-3 px-3 text-right">Define hrs</th>
              <th className="py-3 px-3 text-right">BREX hrs</th>
              <th className="py-3 px-3 text-right">Total hrs</th>
            </tr>
          </thead>
          <tbody>
            {brex.categoryRows.map((row) => (
              <tr
                key={row.id}
                className={"border-b border-gray-200 " + (row.included ? "hover:bg-[#F9FAFB]" : "bg-gray-50/70 text-gray-400")}
                data-testid={`brex-cat-row-${row.id}`}
              >
                <td className="py-2 px-3">
                  <button
                    onClick={() => toggleBrexCategory(row.id)}
                    data-testid={`brex-toggle-${row.id}`}
                    className={
                      "rounded-none px-2 py-1 text-xs font-bold transition-colors inline-flex items-center gap-1 " +
                      (row.included
                        ? "bg-black text-white hover:bg-[#002FA7]"
                        : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-100")
                    }
                  >
                    {row.included ? <CheckSquare size={12} /> : <Square size={12} />}
                    {row.included ? "IN" : "OUT"}
                  </button>
                </td>
                <td className="py-2 px-3">
                  <div className="font-bold text-black">{row.name}</div>
                  <div className="text-[10px] text-gray-500">{row.description}</div>
                </td>
                <td className="py-2 px-3 font-mono text-right text-gray-500" data-testid={`brex-cat-${row.id}-default-dps`}>
                  {row.defaultDps}
                </td>
                <td className="py-2 px-3 text-right">
                  <input
                    type="number" min="0" step="1"
                    value={
                      plan.categoryDpOverrides?.[row.id] !== undefined
                        ? plan.categoryDpOverrides[row.id]
                        : row.defaultDps
                    }
                    onChange={(e) => setBrexCategoryDps(row.id, e.target.value)}
                    data-testid={`brex-cat-${row.id}-dps`}
                    className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-20 text-right"
                  />
                </td>
                <td className="py-2 px-3 font-mono text-right" data-testid={`brex-cat-${row.id}-define-hrs`}>
                  {fmtHours(row.defineMinutes / 60)}
                </td>
                <td className="py-2 px-3 font-mono text-right" data-testid={`brex-cat-${row.id}-brex-hrs`}>
                  {fmtHours(row.brexMinutes / 60)}
                </td>
                <td className="py-2 px-3 font-mono text-right font-bold text-black" data-testid={`brex-cat-${row.id}-total-hrs`}>
                  {fmtHours(row.totalMinutes / 60)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-3 px-3" colSpan={2}>
                Included DPs: <span data-testid="brex-included-dp-total">{brex.totalDps}</span>
                {" · "} Categories: {brex.includedCategoryCount} / {brex.categoryRows.length}
              </td>
              <td colSpan={2}></td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-define-hours-total">
                {fmtHours(brex.defineHours)}
              </td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-brex-hours-total">
                {fmtHours(brex.brexHours)}
              </td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-total-hours-bottom">
                {fmtHours(brex.totalHours)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* TIME & COST SUMMARY */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-300 p-4" data-testid="brex-stat-define">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Define Effort</p>
          <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
            {fmtHours(brex.defineHours)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
            {brex.totalDps} DPs × {plan.defineMinutesPerDP} min
          </p>
        </div>
        <div className="bg-white border border-gray-300 p-4" data-testid="brex-stat-brex">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">BREX Effort</p>
          <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
            {fmtHours(brex.brexHours)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
            {brex.totalDps} DPs × {plan.brexMinutesPerDP} min
          </p>
        </div>
        <div className="bg-white border border-gray-300 p-4" data-testid="brex-stat-base">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Base Cost</p>
          <p className="font-mono text-2xl font-bold text-black leading-none mt-2">
            {formatCurrency(brex.baseCost)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
            Blended {fmtCurrency(brex.blendedRate, sym)} / hr
          </p>
        </div>
        <div className="bg-white border border-gray-300 p-4" data-testid="brex-stat-grand">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Grand Total</p>
          <p className="font-mono text-2xl font-bold text-[#002FA7] leading-none mt-2">
            {formatCurrency(brex.grandTotal)}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-2">
            Base + OH {formatCurrency(brex.overheadCost)} + Cont {formatCurrency(brex.contingencyCost)}
          </p>
        </div>
      </div>

      {/* RECOMMENDED RESOURCES */}
      <div className="bg-white border border-gray-300" data-testid="brex-roles-table">
        <div className="flex items-start justify-between p-4 border-b border-gray-300">
          <div>
            <div className="flex items-center gap-2">
              <BookOpenCheck size={16} className="text-[#002FA7]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                Recommended Resources
              </p>
            </div>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              BR / BREX Role Mix
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Independent rate &amp; effort table for BR / BREX work. Effort % should sum to 100%.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={
                "border px-3 py-2 min-w-[120px] " +
                (effortValid ? "border-gray-300" : "border-[#FF2400]")
              }
              data-testid="brex-effort-badge"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Effort Σ</p>
              <p className={"font-mono text-lg font-bold mt-0.5 " + (effortValid ? "text-black" : "text-[#FF2400]")}>
                {fmtPercent(effortSum)}
              </p>
            </div>
            <button onClick={resetBrexRoles} data-testid="brex-roles-reset-btn"
              className="bg-white border border-gray-300 text-black hover:bg-gray-100 rounded-none px-3 py-2 font-bold text-xs inline-flex items-center gap-1">
              <RotateCcw size={12} /> Reset
            </button>
            <button onClick={addBrexRole} data-testid="brex-roles-add-btn"
              className="bg-black text-white hover:bg-[#002FA7] rounded-none px-3 py-2 font-bold text-xs inline-flex items-center gap-1">
              <Plus size={12} /> Add Role
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">Role</th>
              <th className="py-2 px-3 text-right">Hourly Rate</th>
              <th className="py-2 px-3 text-right">Effort %</th>
              <th className="py-2 px-3 text-right">Hours</th>
              <th className="py-2 px-3 text-right">Cost</th>
              <th className="py-2 px-3 text-right">% of Base</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {plan.roles.map((r, idx) => {
              const br = brex.roleBreakdown[idx];
              return (
                <tr key={idx} className="border-b border-gray-200 hover:bg-[#F9FAFB]">
                  <td className="py-2 px-3">
                    <input value={r.name} onChange={(e) => updateBrexRole(idx, "name", e.target.value)}
                      data-testid={`brex-role-${idx}-name`}
                      className="border border-gray-300 rounded-none px-2 py-1 text-sm w-full font-semibold" />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono text-xs text-gray-500">{sym}</span>
                      <input type="number" min="0" step="0.5" value={r.hourlyRate}
                        onChange={(e) => updateBrexRole(idx, "hourlyRate", e.target.value)}
                        data-testid={`brex-role-${idx}-rate`}
                        className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-24 text-right" />
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <input type="number" min="0" max="100" step="0.5" value={r.effortPercent}
                      onChange={(e) => updateBrexRole(idx, "effortPercent", e.target.value)}
                      data-testid={`brex-role-${idx}-effort`}
                      className="border border-gray-300 rounded-none px-2 py-1 font-mono text-sm w-20 text-right" />
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-gray-700">{fmtHours(br?.roleHours || 0)}</td>
                  <td className="py-2 px-3 font-mono text-right text-black font-bold" data-testid={`brex-role-${idx}-cost`}>
                    {formatCurrency(br?.roleCost || 0)}
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-gray-700">{fmtPercent(br?.costPercent || 0)}</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => removeBrexRole(idx)} data-testid={`brex-role-${idx}-remove`}
                      className="text-gray-500 hover:text-[#FF2400] p-1">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td colSpan={2} className="py-3 px-3">Totals</td>
              <td className="py-3 px-3 font-mono text-right normal-case">{fmtPercent(effortSum)}</td>
              <td className="py-3 px-3 font-mono text-right normal-case">{fmtHours(brex.totalHours)}</td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-base-cost-bottom">
                {formatCurrency(brex.baseCost)}
              </td>
              <td className="py-3 px-3 font-mono text-right normal-case">100.0%</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="bg-white border border-gray-300 p-4 flex items-start gap-3" data-testid="brex-tip">
        <FileCode2 size={18} className="text-[#002FA7] mt-0.5 shrink-0" />
        <div className="flex-1 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-600 max-w-2xl">
            <span className="font-bold text-black">Tip.</span> The BR / BREX grand total is reported here independently — flip the toggle below to also fold it into the project Grand Total visible in the header &amp; footer.
          </p>
          <button
            onClick={() => updateBrexPlan("includeInProjectTotal", !plan.includeInProjectTotal)}
            data-testid="brex-merge-toggle"
            className={
              "rounded-none px-4 py-2 font-bold text-xs transition-colors inline-flex items-center gap-2 " +
              (plan.includeInProjectTotal
                ? "bg-[#002FA7] text-white hover:bg-black"
                : "bg-white border border-gray-300 text-black hover:bg-gray-100")
            }
          >
            <GitMerge size={14} />
            {plan.includeInProjectTotal ? "BREX merged into Project Total" : "Merge BREX into Project Total"}
          </button>
        </div>
      </div>

      {/* DEPENDENCY MAP — BR Category × DM type */}
      <div className="bg-white border border-gray-300" data-testid="brex-dep-map">
        <div className="border-b border-gray-300 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            BREX Dependency Map
          </p>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">
            BR Categories × Data Module Types
          </h3>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Indicative coverage. <span className="font-mono">*</span> = applies to every DM type.
            Categories that map to DM types you actually plan to author are highlighted blue.
          </p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">BR Category</th>
              <th className="py-2 px-3 text-left">Maps to DM Types</th>
              <th className="py-2 px-3 text-right">DPs (current issue)</th>
              <th className="py-2 px-3 text-center w-28">Relevant?</th>
            </tr>
          </thead>
          <tbody>
            {brex.categoryRows.map((row) => {
              const mapped = BR_CATEGORY_DM_MAP[row.id] || [];
              const isUniversal = mapped.includes("*");
              const intersect = mapped.filter((c) => c !== "*" && usedDmTypes.has(c));
              const relevant = isUniversal || intersect.length > 0;
              return (
                <tr key={row.id} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`brex-dep-${row.id}`}>
                  <td className="py-2 px-3 font-semibold">{row.name}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {mapped.map((code) => {
                        const used = code === "*" ? usedDmTypes.size > 0 : usedDmTypes.has(code);
                        const label = code === "*" ? "ALL DM TYPES" : (codeToName[code] || code);
                        return (
                          <span
                            key={code}
                            data-testid={`brex-dep-${row.id}-chip-${code}`}
                            className={
                              "border text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 " +
                              (used
                                ? "bg-[#002FA7] text-white border-[#002FA7]"
                                : "bg-white text-gray-500 border-gray-300")
                            }
                            title={label}
                          >
                            {code}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="py-2 px-3 font-mono text-right">{row.dps}</td>
                  <td className="py-2 px-3 text-center">
                    <span
                      data-testid={`brex-dep-${row.id}-relevant`}
                      className={
                        "inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 " +
                        (relevant
                          ? "bg-[#002FA7] text-white"
                          : "bg-gray-100 text-gray-500 border border-gray-300")
                      }
                    >
                      {relevant ? "Yes" : "Optional"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ISSUE COMPARE */}
      <div className="bg-white border border-gray-300" data-testid="brex-issue-compare">
        <div className="border-b border-gray-300 p-4 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ArrowLeftRight size={16} className="text-[#002FA7]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
                Per-Issue Change Impact
              </p>
            </div>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              Issue {plan.s1000dIssue} vs Issue {compareIssue}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              See how decision-point counts shift between S1000D issues and the time impact at your current per-DP rates.
            </p>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
              Compare to
            </label>
            <select
              data-testid="brex-compare-issue-select"
              value={compareIssue}
              onChange={(e) => setCompareIssue(e.target.value)}
              className="border border-gray-300 rounded-none px-2 py-1.5 mt-1 font-mono text-sm bg-white"
            >
              {S1000D_ISSUES.filter((i) => i !== plan.s1000dIssue).map((i) => (
                <option key={i} value={i}>Issue {i}{i === "6.0" ? " (latest)" : ""}</option>
              ))}
            </select>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
              <th className="py-2 px-3 text-left">Category</th>
              <th className="py-2 px-3 text-right">Issue {plan.s1000dIssue}</th>
              <th className="py-2 px-3 text-right">Issue {compareIssue}</th>
              <th className="py-2 px-3 text-right">Δ DPs</th>
              <th className="py-2 px-3 text-right">Δ Hours</th>
            </tr>
          </thead>
          <tbody>
            {delta.rows.map((r) => {
              const sign = r.deltaDps === 0 ? "·" : r.deltaDps > 0 ? "▲" : "▼";
              const color = r.deltaDps === 0 ? "text-gray-400" : r.deltaDps > 0 ? "text-[#FF2400]" : "text-[#15803D]";
              return (
                <tr key={r.id} className="border-b border-gray-200 hover:bg-[#F9FAFB]" data-testid={`brex-delta-row-${r.id}`}>
                  <td className="py-2 px-3 font-semibold">{r.name}</td>
                  <td className="py-2 px-3 font-mono text-right">{r.baseDps}</td>
                  <td className="py-2 px-3 font-mono text-right">{r.targetDps}</td>
                  <td className={"py-2 px-3 font-mono text-right " + color}>
                    {sign} {Math.abs(r.deltaDps)}
                  </td>
                  <td className={"py-2 px-3 font-mono text-right " + color}>
                    {sign} {fmtHours(Math.abs(r.deltaHours))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td className="py-3 px-3">Totals</td>
              <td className="py-3 px-3 font-mono text-right normal-case">{delta.totalBaseDps}</td>
              <td className="py-3 px-3 font-mono text-right normal-case">{delta.totalTargetDps}</td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-delta-total-dps">
                {delta.totalDeltaDps >= 0 ? "+" : ""}{delta.totalDeltaDps}
              </td>
              <td className="py-3 px-3 font-mono text-right normal-case" data-testid="brex-delta-total-hours">
                {delta.totalDeltaHours >= 0 ? "+" : "−"}{fmtHours(Math.abs(delta.totalDeltaHours))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* BREX SKELETON EXPORT */}
      <div className="bg-white border border-gray-300 p-4 flex items-start justify-between gap-4 flex-wrap" data-testid="brex-skeleton-panel">
        <div>
          <div className="flex items-center gap-2">
            <FileCode2 size={16} className="text-[#002FA7]" />
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              BREX Skeleton Generator
            </p>
          </div>
          <h3 className="font-heading text-lg tracking-tight font-bold text-black">
            Starter .brex.xml
          </h3>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Generate a placeholder S1000D BREX data module with structureObjectRuleGroup blocks
            for each included BR category and indicative XPath paths your team can refine. Designed
            for Issue {plan.s1000dIssue} — placeholders are illustrative, not schema-validated.
          </p>
        </div>
        <button
          onClick={() => exportBrexSkeleton(estimate, brex)}
          data-testid="brex-skeleton-export-btn"
          className="bg-[#002FA7] text-white hover:bg-black rounded-none px-4 py-2.5 font-bold text-sm inline-flex items-center gap-2"
        >
          <Download size={14} /> Download BREX Skeleton (.brex.xml)
        </button>
      </div>
    </section>
  );
}
